import { BUILD_VERSION, isLocalBuild } from '@/utils/buildVersion';
import {
  BuildVersionState,
  fetchBuildVersion,
  resolveBuildVersionState,
} from '@/utils/buildVersionCheck';
import { addBreadcrumb } from '@sentry/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { minutesToMilliseconds } from 'date-fns';

const CHECK_INTERVAL = minutesToMilliseconds(5);

const buildVersionKeys = {
  check: ['buildVersion', 'check'] as const,
};

const UNKNOWN: BuildVersionState = { status: 'unknown' };

/**
 * Compares the running bundle with the one the hosting currently serves.
 * Checks on mount, when the tab regains visibility and on a timer while it is
 * visible, never more than once per `CHECK_INTERVAL`. Unavailable checks are
 * breadcrumbs rather than exceptions: they are expected noise around deploys
 * and flaky connections, and the query itself never errors.
 */
export const useBuildVersionCheck = (): BuildVersionState => {
  const queryClient = useQueryClient();

  const { data } = useQuery({
    queryKey: buildVersionKeys.check,
    queryFn: async () => {
      const check = await fetchBuildVersion();
      if (check.outcome === 'unavailable') {
        addBreadcrumb({
          category: 'build-version',
          level: 'warning',
          message: 'Build version check unavailable',
          data: { reason: check.reason },
        });
      }
      const previous =
        queryClient.getQueryData<BuildVersionState>(buildVersionKeys.check) ??
        UNKNOWN;
      return resolveBuildVersionState(
        previous,
        BUILD_VERSION,
        check,
        Date.now()
      );
    },
    enabled: !isLocalBuild,
    staleTime: CHECK_INTERVAL,
    refetchInterval: CHECK_INTERVAL,
  });

  return data ?? UNKNOWN;
};
